/*
 * The speech script: what the voice is given, block by block.
 *
 * Shared byte for byte between know-web and know-mobile (like lib/book/*), so a
 * segment, a sentence and a word have the same id and the same character range
 * on both, which is what lets a listening position survive edits and move
 * between devices. Dependency-free on purpose.
 *
 * Each app turns its own corpus into ListenSourceBlocks; buildScript turns those
 * into segments carrying the display text, the spoken text the engine reads, a
 * map from spoken characters back to display characters, sentence ranges, the
 * spoken words the engine reports timings for, and a content hash.
 */

export const NORMALISER_VERSION = 'v1';

export type ListenBlockKind =
  | 'title'
  | 'heading'
  | 'paragraph'
  | 'listItem'
  | 'quote'
  | 'caption'
  | 'code'
  | 'table'
  /** An element (a chart, a diagram, a slide show) drawn on the page: its text is its name line. */
  | 'element'
  | 'other';

export interface ListenSourceBlock {
  /** Stable id within the document: the block id, or a derived one when the block has none. */
  id: string;
  /** The section the block belongs to; null for the document's title and overview. */
  sectionId: string | null;
  sectionNumber: number | null;
  kind: ListenBlockKind;
  /** Display text exactly as the app's corpus has it. */
  text: string;
  /** An element's words, read after its name when the rules say so. They are not on the page as text. */
  parts?: readonly { kind: ListenBlockKind; text: string }[];
}

export interface ReadRules {
  /** Say "Section 3." before each section's title. */
  numbers: boolean;
  /** Say the category after each section's title. */
  categories: boolean;
  code: 'skip' | 'announce' | 'read';
  tables: 'summary' | 'rows';
  quotes: 'attribute' | 'text';
  /** At an element: say its name and title, then its words too, or pass it in silence. */
  elements: 'name' | 'words' | 'skip';
}

export const DEFAULT_READ_RULES: ReadRules = {
  numbers: false,
  categories: false,
  code: 'skip',
  tables: 'summary',
  quotes: 'attribute',
  elements: 'name',
};

/** [spokenStart, spokenEnd, displayStart, displayEnd, rule]; rule is null where the text is unchanged. */
export type Piece = [number, number, number, number, string | null];

export interface SpokenWord {
  /** Range in `spoken`. */
  s: number;
  e: number;
  /** Range in the display text this word highlights. */
  ds: number;
  de: number;
}

export interface ListenSegment {
  id: string;
  blockId: string;
  sectionId: string | null;
  sectionNumber: number | null;
  kind: ListenBlockKind;
  /** Display text. */
  text: string;
  /** What the engine reads. Empty for a skipped block. */
  spoken: string;
  skip: boolean;
  pieces: Piece[];
  /** Sentence ranges in the display text. */
  sentences: [number, number][];
  /** The engine reports one timing per entry, in order: the /\S+/ runs of `spoken`. */
  words: SpokenWord[];
  /** Content hash of everything that changes the sound; the cache key. */
  hash: string;
}

export interface ListenScript {
  normaliser: string;
  segments: ListenSegment[];
}

/** House lexicon: written form to what is said. The reader's own list is applied after it and wins. */
export const HOUSE_LEXICON: Readonly<Record<string, string>> = {
  'know.sh': 'know dot S H',
  'k.': 'kay',
  knowell: 'know well',
  OAuth: 'oh auth',
  JSON: 'jason',
  SQL: 'S Q L',
  API: 'A P I',
  APIs: 'A P Is',
  iOS: 'eye O S',
  MCP: 'M C P',
  README: 'read me',
  npm: 'N P M',
  UI: 'U I',
  id: 'eye dee',
  ID: 'eye dee',
  IDs: 'eye dees',
  uri: 'U R I',
  URI: 'U R I',
  URIs: 'U R Is',
  URL: 'U R L',
  URLs: 'U R Ls',
  HTTP: 'H T T P',
  HTTPS: 'H T T P S',
  HTML: 'H T M L',
  CSS: 'C S S',
};

const DIGITS = ['oh', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
const EXT: Readonly<Record<string, string>> = {
  md: 'markdown', ts: 'T S', tsx: 'T S X', js: 'J S', json: 'jason', cs: 'C sharp', py: 'python', yaml: 'yaml', yml: 'yaml', sh: 'shell',
};

const hostSay = (h: string) => h.replace(/^www\./, '').split('.').join(' dot ');
/*
 * A code name said as words: "useLibrary" is "use library", "user_id" "user id". A run of
 * capitals inside a mixed-case name stays one word in capitals ("getAPIKey" is "get API key",
 * "HTTPClient" "HTTP client", "getAPIs" "get APIs") for the lexicon or the voice to spell; a name
 * that is all capitals is lowercased as before.
 */
const camel = (s: string) => {
  const name = s.replace(/\(.*\)$/, '');
  const mixed = /[a-z]/.test(name);
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z](?!s\b)[a-z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .split(' ')
    .map(w => (mixed && /^[A-Z][A-Z0-9]+s?$/.test(w) ? w : w.toLowerCase()))
    .join(' ');
};
const escRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function urlHost(u: string): string | null {
  const m = /^https?:\/\/([^/?#:]+)/i.exec(u);
  return m?.[1] ?? null;
}

/*
 * Numbers joined by a colon or grouped by commas. The voices' own normalisers read a lone
 * number but leave "4:4" and "1,000" alone, and Kokoro's letter-to-sound model on iOS has no
 * digits, so each such digit run came out as "ex": "James 4:4" was "James ex ex". The script
 * says them in words for every engine.
 */
const ONES = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve',
  'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
const SCALES: readonly [number, string][] = [[1e9, 'billion'], [1e6, 'million'], [1e3, 'thousand']];

/** A whole number in words, spaced as the voices read them: 24 is "twenty four". */
export function numberWords(n: number): string {
  if (n < 20) return ONES[n] ?? '';
  if (n < 100) return n % 10 ? `${TENS[Math.floor(n / 10)]} ${ONES[n % 10]}` : (TENS[n / 10] ?? '');
  if (n < 1000) return `${ONES[Math.floor(n / 100)]} hundred${n % 100 ? ` ${numberWords(n % 100)}` : ''}`;
  const [size, name] = SCALES.find(([v]) => n >= v) ?? [1e3, 'thousand'];
  return `${numberWords(Math.floor(n / size))} ${name}${n % size ? ` ${numberWords(n % size)}` : ''}`;
}

/** Digits as written: a leading zero is said "oh", digit by digit ("05" is "oh five"). */
const digitsSay = (d: string) => (d.length > 1 && d.startsWith('0') ? [...d].map(c => (c === '0' ? 'oh' : ONES[Number(c)])).join(' ') : numberWords(Number(d)));

/** A clock time: 4:30 "four thirty", 4:05 "four oh five", 4:00 "four o'clock", 13:00 "thirteen hundred". */
function clockSay(h: string, min: string): string {
  const hour = numberWords(Number(h));
  if (min === '00') return Number(h) <= 12 ? `${hour} o'clock` : `${hour} hundred`;
  return `${hour} ${min.startsWith('0') ? `oh ${ONES[Number(min[1])]}` : numberWords(Number(min))}`;
}

/** Capitalised words before a colon number that are not books: "At 4:30", "Monday 10:15", "Figure 3:1". */
const NOT_BOOKS = new Set(
  ('at by from to until till before after around about since between and or the on in is it noon midnight today tonight ' +
    'tomorrow yesterday opens closes starts ends doors time ratio odds score monday tuesday wednesday thursday friday ' +
    'saturday sunday january february march april may june july august september october november december ' +
    'figure fig table page step level version line room gate track platform')
    .split(' '),
);

/** One written part of a match and what is said for it: [start, end) within the match. */
type Part = [number, number, string];

interface Rule {
  id: string;
  re: RegExp;
  say: (m: RegExpExecArray) => string;
  /** Characters at the start of the match that stay as written (the context a rule looked at); below zero drops the match. */
  from?: (m: RegExpExecArray) => number;
  /**
   * The match said part by part, each part's words mapped to its own written characters, so the
   * highlight steps through "350", "-", "375" and "°F" as they are said instead of sitting on the
   * whole run. The words are joined with spaces; `say` is the same words.
   */
  parts?: (m: RegExpExecArray) => Part[];
}

/*
 * Temperatures. The voices say "350°F" as "three hundred fifty F" (the degree sign is dropped),
 * "180℃" with no scale at all, "-18°C" without the minus on iOS, and "350-375°F" as
 * "three hundred fifty three hundred seventy five F". A temperature is also one written word
 * the iOS voice says as several, which its word timing cannot pair up, so the highlight ran
 * ahead of the voice through the rest of the sentence. The script says the whole temperature
 * in plain words: one written word for every word the voice says.
 */
const NUM = String.raw`[-−]?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?`;
const DEGREE_UNIT = String.raw`(?:[°º˚]\s?(?:F(?:ahrenheit)?|C(?:elsius|entigrade)?|[fc])(?![A-Za-z])|°(?![A-Za-z])|℉|℃|degrees?\s+(?:F|C|Fahrenheit|Celsius|Centigrade)(?![A-Za-z]))`;
const TEMPERATURE = new RegExp(
  String.raw`(?<![\w.,:−-])(${NUM})(\s?)(${DEGREE_UNIT})?(?:(\s?[-–—]\s?|\s+to\s+)(${NUM}))?(\s?)(${DEGREE_UNIT})(?=(\s(?:angle|turn|bend|rotation)\b)?)`,
  'g',
);

/** A written number in words: -18 "minus eighteen", 38.5 "thirty eight point five", 1,200 "one thousand two hundred". */
function numberSay(written: string): string {
  const [whole = '', frac] = written.replace(/^[-−]/, '').replace(/,/g, '').split('.');
  const sign = /^[-−]/.test(written) ? 'minus ' : '';
  return `${sign}${numberWords(Number(whole))}${frac ? ` point ${[...frac].map(d => ONES[Number(d)]).join(' ')}` : ''}`;
}

/** "°F" "degrees Fahrenheit", "℃" "degrees Celsius", a bare "°" "degrees"; "degree" after a written 1 or before a noun (a 90° angle). */
function degreeSay(unit: string, value: string | null, beforeNoun = false): string {
  const u = unit.replace(/^degrees?\s+/, '').replace(/^[°º˚]\s?/, '');
  const scale = /^(?:[Ff]|Fahrenheit|℉)$/.test(u) ? ' Fahrenheit' : u === 'Centigrade' ? ' Centigrade' : /^(?:[Cc]|Celsius|℃)$/.test(u) ? ' Celsius' : '';
  return `${beforeNoun || (value !== null && /^[-−]?1$/.test(value)) ? 'degree' : 'degrees'}${scale}`;
}

function temperatureParts(m: RegExpExecArray): Part[] {
  const [, from = '', gap1 = '', unit1, sep, to, gap2 = '', unit2 = '', noun] = m;
  const out: Part[] = [];
  let at = 0;
  const part = (written: string | undefined, said: string | null) => {
    if (!written) return;
    if (said !== null) out.push([at, at + written.length, said]);
    at += written.length;
  };
  part(from, numberSay(from));
  part(gap1, null);
  part(unit1, unit1 ? degreeSay(unit1, sep ? null : from) : null);
  part(sep, 'to');
  part(to, to ? numberSay(to) : null);
  part(gap2, null);
  part(unit2, degreeSay(unit2, sep ? null : from, !!noun));
  return out;
}

/** Quantities a hyphenated range is read before: "2-3 cups" is "2 to 3 cups"; "555-1234" is left alone. */
const RANGE_UNITS =
  'seconds?|secs?|minutes?|mins?|hours?|hrs?|days?|nights?|weeks?|months?|years?|cups?|tablespoons?|tbsp|teaspoons?|tsp|' +
  'ounces?|oz|pounds?|lbs?|grams?|g|kg|ml|liters?|litres?|inches?|cm|mm|servings?|people|pieces?|slices?|cloves?|times';

export const RULES: readonly Rule[] = [
  { id: 'url', re: /\bhttps?:\/\/[^\s)]+/g, say: m => { const h = urlHost(m[0]); return h ? `a link to ${hostSay(h)}` : 'a link'; } },
  { id: 'file', re: /\b[\w-]+\.(md|tsx?|js|json|cs|py|ya?ml|sh)\b/g, say: m => { const ext = m[1] ?? ''; return `${camel(m[0].slice(0, -(ext.length + 1)))} ${EXT[ext] ?? ext}`; } },
  { id: 'status', re: /\b(HTTP|status)\s([1-5]\d\d)\b/g, say: m => `${m[1] === 'HTTP' ? 'H T T P' : 'status'} ${[...(m[2] ?? '')].map(d => DIGITS[Number(d)]).join(' ')}` },
  { id: 'version', re: /\bv(\d+(?:\.\d+)+)\b/g, say: m => `version ${(m[1] ?? '').split('.').join(' point ')}` },
  { id: 'call', re: /\b[a-z]+(?:[A-Z][a-z0-9]*)+\([^)]*\)/g, say: m => camel(m[0]) },
  { id: 'camel', re: /\b[a-z]+(?:[A-Z][a-z0-9]*)+\b/g, say: m => camel(m[0]) },
  // UserId, HttpClient, HTTPClient, ChatGPT. The first part is three letters or more, or a run of
  // capitals before one, so PhD, MHz, NaCl, DeVito and McDonald stay whole; so do Mac, Van, Von
  // and Fitz surnames (MacArthur, VanDyke) and a word with one capital after it (SaaS, SpaceX).
  {
    id: 'pascal',
    re: /\b(?!(?:Mac|Van|Von|Fitz)[A-Z]|[A-Z][a-z]+[A-Z]\b)(?:[A-Z][a-z]{2,}[a-z0-9]*[A-Z]|[A-Z]{2,}[a-z]{2})[A-Za-z0-9]*\b/g,
    say: m => camel(m[0]),
  },
  { id: 'snake', re: /\b[a-z]+(?:_[a-z0-9]+)+\b/g, say: m => camel(m[0]) },
  // 10:15 am, 4:30 p.m.: "ten fifteen eh em", keeping the stop of a.m. only where it ends the
  // sentence. Spelled as words: web misaki reads a capital A there as the article.
  {
    id: 'time',
    re: /(?<![\d:.])(1[0-2]|0?[1-9]):([0-5]\d)\s?([AaPp])\.?[Mm](?![A-Za-z])(?:(\.)(?=\s+[A-Z"“]|\s*$)|\.)?/g,
    say: m => `${clockSay(m[1] ?? '', m[2] ?? '')} ${/a/i.test(m[3] ?? '') ? 'eh' : 'pee'} em${m[4] ?? ''}`,
  },
  // James 4:4 "four, verse four"; Genesis 1:1-5 "one, verses one to five"; John 1:1-2:3
  // "one, verse one to two, verse three". A verse has no leading zero; the book is any
  // capitalised word that is not a preposition, a day or a month.
  {
    id: 'verse',
    re: /\b([A-Z][A-Za-z]+\.?\s+)(\d{1,3}):([1-9]\d{0,2})(?:\s?[-–]\s?(\d{1,3})(?::([1-9]\d{0,2}))?)?(?![\d:])/g,
    from: m => (NOT_BOOKS.has((m[1] ?? '').trim().replace(/\.$/, '').toLowerCase()) ? -1 : (m[1] ?? '').length),
    say: m => {
      const [, , ch = '', v = '', to = '', toVerse] = m;
      if (toVerse) return `${numberWords(Number(ch))}, verse ${numberWords(Number(v))} to ${numberWords(Number(to))}, verse ${numberWords(Number(toVerse))}`;
      return to
        ? `${numberWords(Number(ch))}, verses ${numberWords(Number(v))} to ${numberWords(Number(to))}`
        : `${numberWords(Number(ch))}, verse ${numberWords(Number(v))}`;
    },
  },
  // a 3:1 ratio, odds of 5:2: "three to one". Only where the words say it is a ratio.
  { id: 'ratio', re: /(?<![\d:.])(\d{1,3}):(\d{1,3})(?![\d:])(?=\s+(?:ratio|odds|majority|margin|split)\b)/gi, say: m => `${numberWords(Number(m[1]))} to ${numberWords(Number(m[2]))}` },
  {
    id: 'ratio',
    re: /\b((?:ratio|odds)\s+(?:of\s+)?)(\d{1,3}):(\d{1,3})(?![\d:])/gi,
    from: m => (m[1] ?? '').length,
    say: m => `${numberWords(Number(m[2]))} to ${numberWords(Number(m[3]))}`,
  },
  // 4:30, 13:05: a clock time, read as one where the minutes have two digits.
  { id: 'time', re: /(?<![\d:.])([01]?\d|2[0-3]):([0-5]\d)(?![\d:])/g, say: m => clockSay(m[1] ?? '', m[2] ?? '') },
  // Anything else joined by colons, a range after it included: 4:4 "four four", 1:1-5 "one one to five".
  {
    id: 'colon',
    re: /(?<![\d:.])(\d{1,4}(?::\d{1,4})+)(?:\s?[-–]\s?(\d{1,4}))?(?![\d:])/g,
    say: m => `${(m[1] ?? '').split(':').map(digitsSay).join(' ')}${m[2] ? ` to ${digitsSay(m[2])}` : ''}`,
  },
  // 1,000 and 12,500.75, not after a currency sign (the engines say money themselves).
  {
    id: 'grouped',
    re: /(?<![\d.,$£€¥])(\d{1,3}(?:,\d{3})+)(?:\.(\d+))?(?![\d]|[.,]\d)/g,
    say: m => `${numberWords(Number((m[1] ?? '').replace(/,/g, '')))}${m[2] ? ` point ${[...m[2]].map(d => ONES[Number(d)]).join(' ')}` : ''}`,
  },
  // 350°F "three hundred fifty degrees Fahrenheit", 180℃, -18°C "minus eighteen degrees Celsius",
  // 350-375°F "three hundred fifty to three hundred seventy five degrees Fahrenheit", 400 degrees F.
  {
    id: 'temperature',
    re: TEMPERATURE,
    say: m => temperatureParts(m).map(p => p[2]).join(' '),
    parts: temperatureParts,
  },
  // 350°F/175°C: the conversion after the slash is said as an alternative, not run into the scale.
  { id: 'temperature', re: /(°\s?[FfCc]|[℉℃])(\s?\/\s?)(?=[-−]?\d)/g, from: m => (m[1] ?? '').length, say: () => ' or ' },
  // 25–30 minutes "25 to 30 minutes"; with a hyphen only before a quantity (2-3 cups), since
  // 555-1234 and 2026-09-27 are not ranges. The numbers stay for the voice to say.
  {
    id: 'range',
    re: new RegExp(
      String.raw`(?<![\w.,:–—−-])(\d+(?:\.\d+)?)(\s?[–—]\s?|-(?=\d+(?:\.\d+)?\s(?:${RANGE_UNITS})\b))(?=\d+(?:\.\d+)?(?![\w:–—−-]|[.,]\d))`,
      'g',
    ),
    from: m => (m[1] ?? '').length,
    say: () => ' to ',
  },
];

/** Rules that say a code name, whose words are looked up in the lexicon. */
const CODE_NAME_RULES = new Set(['file', 'call', 'camel', 'pascal', 'snake']);

interface Hit {
  s: number;
  e: number;
  say: string;
  rule: string;
  /** Absolute written ranges said part by part (Rule.parts). */
  parts?: Part[];
}

function allMatches(re: RegExp, text: string): RegExpExecArray[] {
  const out: RegExpExecArray[] = [];
  const g = new RegExp(re.source, re.flags.includes('g') ? re.flags : `${re.flags}g`);
  let m: RegExpExecArray | null;
  while ((m = g.exec(text))) {
    out.push(m);
    if (m[0].length === 0) g.lastIndex++;
  }
  return out;
}

/** Rewrite only what the voice would read badly, and keep a map back to the display text. */
export function normalise(text: string, lexicon: Readonly<Record<string, string>> = {}): { spoken: string; pieces: Piece[] } {
  const lex: Record<string, string> = { ...HOUSE_LEXICON, ...lexicon };
  // A code name has lost its case by the time it is words ("userId" is "user id"), so each of its
  // words is also looked up lowercased; the reader's entries still win.
  const lower = new Map(Object.entries(lex).map(([w, say]) => [w.toLowerCase(), say]));
  const sayName = (said: string) => said.split(' ').map(w => (Object.hasOwn(lex, w) ? lex[w] : lower.get(w.toLowerCase())) ?? w).join(' ');
  const hits: Hit[] = [];
  for (const r of RULES) {
    for (const m of allMatches(r.re, text)) {
      const from = r.from?.(m) ?? 0;
      if (from < 0) continue;
      const parts = r.parts?.(m).map(([a, b, say]): Part => [m.index + a, m.index + b, say]);
      const said = CODE_NAME_RULES.has(r.id) ? sayName(r.say(m)) : r.say(m);
      hits.push({ s: m.index + from, e: m.index + m[0].length, say: said, rule: r.id, ...(parts ? { parts } : {}) });
    }
  }
  for (const [w, say] of Object.entries(lex)) {
    const re = new RegExp(`(?<![\\w.])${escRe(w)}(?![\\w])`, 'g');
    for (const m of allMatches(re, text)) hits.push({ s: m.index, e: m.index + m[0].length, say, rule: w in lexicon ? 'reader' : 'lexicon' });
  }
  const rank = (h: Hit) => (h.rule === 'reader' ? 0 : h.rule === 'lexicon' ? 1 : 2);
  hits.sort((a, b) => a.s - b.s || b.e - b.s - (a.e - a.s) || rank(a) - rank(b));
  const chosen: Hit[] = [];
  let end = 0;
  for (const h of hits) if (h.s >= end) { chosen.push(h); end = h.e; }
  let spoken = '';
  let cursor = 0;
  const pieces: Piece[] = [];
  for (const h of chosen) {
    if (h.s > cursor) { pieces.push([spoken.length, spoken.length + h.s - cursor, cursor, h.s, null]); spoken += text.slice(cursor, h.s); }
    if (h.parts?.length) {
      // Each part's words (and the space after them) map to that part's written characters.
      h.parts.forEach(([a, b, say], k, all) => {
        const said = k < all.length - 1 ? `${say} ` : say;
        pieces.push([spoken.length, spoken.length + said.length, a, b, h.rule]);
        spoken += said;
      });
    } else {
      pieces.push([spoken.length, spoken.length + h.say.length, h.s, h.e, h.rule]);
      spoken += h.say;
    }
    cursor = h.e;
  }
  if (cursor < text.length) { pieces.push([spoken.length, spoken.length + text.length - cursor, cursor, text.length, null]); spoken += text.slice(cursor); }
  return { spoken, pieces };
}

/** Map a spoken range back to the display range it came from. A word inside a rewritten phrase maps to the whole phrase. */
export function toDisplay(pieces: readonly Piece[], a: number, b: number): [number, number] {
  const at = (i: number, edge: 'start' | 'end') => {
    const p = pieces.find(([s, e]) => i >= s && i < e) ?? pieces[pieces.length - 1];
    if (!p) return 0;
    if (p[4]) return edge === 'start' ? p[2] : p[3];
    return p[2] + (i - p[0]) + (edge === 'end' ? 1 : 0);
  };
  return [at(a, 'start'), at(Math.max(a, b - 1), 'end')];
}

/** Sentence ranges: a break after . ! ? or ; followed by space and a capital or a quote. */
export function sentences(text: string): [number, number][] {
  const out: [number, number][] = [];
  let start = 0;
  for (const m of allMatches(/(?<=[.!?;])\s+(?=[A-Z"'“])/g, text)) {
    out.push([start, m.index]);
    start = m.index + m[0].length;
  }
  out.push([start, text.length]);
  return out.filter(([s, e]) => text.slice(s, e).trim().length > 0);
}

/** Trim leading and trailing punctuation from a word's display range so the highlight sits on the letters. */
function trimPunct(text: string, s: number, e: number): [number, number] {
  while (s < e && /[\s"'‘’“”([{]/.test(text.charAt(s))) s++;
  while (e > s && /[\s"'‘’“”.,;:!?)\]}]/.test(text.charAt(e - 1))) e--;
  return [s, e];
}

export function spokenWords(spoken: string, pieces: readonly Piece[], display: string): SpokenWord[] {
  return allMatches(/\S+/g, spoken).map(m => {
    const [a, b] = toDisplay(pieces, m.index, m.index + m[0].length);
    const [ds, de] = trimPunct(display, a, b);
    return { s: m.index, e: m.index + m[0].length, ds, de: Math.max(ds, de) };
  });
}

/** cyrb53: a small, fast, deterministic string hash, the same in every JS runtime. */
export function hash53(str: string, seed = 0): string {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, '0');
}

export interface ScriptOptions {
  voice: string;
  engine: string;
  rules?: ReadRules;
  lexicon?: Readonly<Record<string, string>>;
  /** Category names by section id, for rules.categories. */
  categories?: Readonly<Record<string, string>>;
}

/** Turn a document's blocks, in reading order, into the segments the voice reads. */
export function buildScript(blocks: readonly ListenSourceBlock[], opts: ScriptOptions): ListenScript {
  const rules = opts.rules ?? DEFAULT_READ_RULES;
  const lexicon = opts.lexicon ?? {};
  const segments: ListenSegment[] = [];
  const seen = new Map<string, number>();
  const lexKey = Object.keys(lexicon).sort().map(k => `${k}=${lexicon[k]}`).join(';');
  const push = (b: ListenSourceBlock, text: string, spoken: string, pieces: Piece[], skip: boolean) => {
    const n = seen.get(b.id) ?? 0;
    seen.set(b.id, n + 1);
    segments.push({
      id: n ? `${b.id}~${n}` : b.id,
      blockId: b.id,
      sectionId: b.sectionId,
      sectionNumber: b.sectionNumber,
      kind: b.kind,
      text,
      spoken,
      skip,
      pieces,
      sentences: skip ? [] : sentences(text),
      words: skip ? [] : spokenWords(spoken, pieces, text),
      hash: hash53([opts.engine, opts.voice, NORMALISER_VERSION, lexKey, spoken].join('|')),
    });
  };
  /** What the voice says for a block of text, by the rules; null when the block is not read at all. */
  const say = (b: ListenSourceBlock, kind: ListenBlockKind, text: string): { spoken: string; pieces: Piece[]; skip: boolean } | null => {
    if (kind === 'code' && rules.code !== 'read') {
      const lines = text.split('\n').length;
      return { spoken: rules.code === 'announce' ? `Code, ${lines === 1 ? 'one line' : `${lines} lines`}.` : '', pieces: [], skip: true };
    }
    if (kind === 'table' && rules.tables === 'summary') {
      const rows = text.split('\n').filter(r => r.trim());
      const head = rows[0] ?? '';
      return { spoken: `A table: ${head.replace(/\s*\|\s*|\t+/g, ', ')}. ${rows.length - 1 === 1 ? 'One row' : `${rows.length - 1} rows`}.`, pieces: [], skip: true };
    }
    if (kind === 'caption' && rules.quotes === 'text') return null;
    const pre = kind === 'heading' && rules.numbers && b.sectionNumber != null ? `Section ${b.sectionNumber}. ` : '';
    const cat = kind === 'heading' && rules.categories && b.sectionId ? opts.categories?.[b.sectionId] : undefined;
    const post = cat ? ` ${cat}.` : '';
    const n2 = normalise(text, lexicon);
    const pieces = n2.pieces.map(p => [p[0] + pre.length, p[1] + pre.length, p[2], p[3], p[4]] as Piece);
    if (pre) pieces.unshift([0, pre.length, 0, 0, 'setting']);
    const spoken = pre + n2.spoken + post;
    if (post) pieces.push([spoken.length - post.length, spoken.length, text.length, text.length, 'setting']);
    return { spoken, pieces, skip: false };
  };
  for (const b of blocks) {
    const text = b.text.replace(/\s+$/, '');
    if (!text.trim()) continue;
    if (b.kind === 'element') {
      // An element has no words on the page to follow: the voice names it (its line runs
      // along the element's foot), then reads its words under the same line, or passes it.
      if (rules.elements === 'skip') continue;
      const name = normalise(text, lexicon).spoken;
      push(b, text, /[.!?]$/.test(name) ? name : `${name}.`, [], true);
      if (rules.elements === 'words') {
        for (const part of b.parts ?? []) {
          const t = part.text.replace(/\s+$/, '');
          const said = t.trim() ? say(b, part.kind, t) : null;
          if (said?.spoken) push(b, t, said.spoken, [], true);
        }
      }
      continue;
    }
    const said = say(b, b.kind, text);
    if (said) push(b, text, said.spoken, said.pieces, said.skip);
  }
  return { normaliser: NORMALISER_VERSION, segments };
}

/** Where a character offset in a block falls: the segment and the first spoken word at or after it. */
export function locateOffset(script: ListenScript, blockId: string, offset = 0): { segment: number; word: number } {
  const i = script.segments.findIndex(s => s.blockId === blockId);
  if (i < 0) return { segment: 0, word: 0 };
  const seg = script.segments[i];
  if (!seg) return { segment: 0, word: 0 };
  const w = seg.words.findIndex(x => x.de > offset);
  return { segment: i, word: Math.max(0, w) };
}
